import LegSectionAncillaryAccordion, {
  type LegSectionAncillaryAccordionProps,
} from '@/components/purchase/checkout/ancillaries/LegSectionAncillaryAccordion';
import LegOriginDestination from '@/components/purchase/checkout/LegOriginDestination';
import { useAvailableAncillaries } from '@/hooks/useAvailableAncillaries';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import { TransText } from '@/i18n/trans/text';
import type { BookedAncillaryDTO, LegDTO } from '@/types/dto/agent';
import {
  createAncillaryProductKey,
  isBundledAncillary,
} from '@/utils/ancillary';
import { Button, Icons } from '@packages/webshop-design-system';
import { type FC, useMemo } from 'react';

interface LegSectionProps {
  leg: LegDTO;
  classNames?: { accordionItem?: string };
  handleRemoveAncillary: (ancillary: BookedAncillaryDTO) => Promise<void>;
  changeSelectedLeg: () => void;
}

type LegSectionAncillaryRow =
  LegSectionAncillaryAccordionProps['ancillaryRows'][number] & {
    productKey: string;
    isBundled: boolean;
    isVisible: boolean;
    isPreviouslyBooked: boolean;
  };

const LegSection: FC<LegSectionProps> = ({
  leg,
  classNames,
  handleRemoveAncillary,
  changeSelectedLeg,
}) => {
  const booking = useCheckoutBooking();
  const { changeFlow } = useChangeBookingSelection();
  const availableAncillaries = useAvailableAncillaries(leg.id);

  const ancillaryRows = useMemo<Array<LegSectionAncillaryRow>>(() => {
    const ancillaryRows =
      booking?.bookedTrips?.flatMap(
        ({ bookedOffers }) =>
          bookedOffers?.flatMap(
            ({ admissions }) =>
              admissions?.flatMap(({ coveredLegIds, ancillaries }) => {
                if (!leg.id || !coveredLegIds?.includes(leg.id)) {
                  return [];
                }
                return (
                  ancillaries?.map((ancillary) => {
                    return {
                      ancillary,
                      productKey: createAncillaryProductKey(ancillary),
                      isBundled: isBundledAncillary(ancillary),
                      isVisible: true,
                      isPreviouslyBooked: false,
                      isRemoveDisabled: false,
                    };
                  }) ?? []
                );
              }) ?? []
          ) ?? []
      ) ?? [];

    return (
      ancillaryRows
        // Bundled extras  can't be removed
        .map((ancillaryRow) => ({
          ...ancillaryRow,
          isRemoveDisabled: ancillaryRow.isBundled,
        }))
        // In change travel class flow originally purchased extras that can be,
        // are carried over with the new offer and these can't be removed.
        // Also, hide extra with `EXCHANGE_ONGOING` status, if it's already carried to the new offer.
        .map((ancillaryRow) => {
          if (changeFlow === 'changeTravelClass') {
            if (ancillaryRow.ancillary.status === 'PREBOOKED') {
              const hasExchangeOngoingAncillary = ancillaryRows.some(
                ({ productKey, ancillary }) =>
                  productKey === ancillaryRow.productKey &&
                  ancillary.status === 'EXCHANGE_ONGOING'
              );
              if (hasExchangeOngoingAncillary) {
                return {
                  ...ancillaryRow,
                  isPreviouslyBooked: true,
                  isRemoveDisabled: true,
                };
              }
            } else if (ancillaryRow.ancillary.status === 'EXCHANGE_ONGOING') {
              const hasPrebookedAncillary = ancillaryRows.some(
                ({ productKey, ancillary }) =>
                  productKey === ancillaryRow.productKey &&
                  ancillary.status === 'PREBOOKED'
              );
              return {
                ...ancillaryRow,
                isVisible: !hasPrebookedAncillary,
              };
            }
          }
          return ancillaryRow;
        })
    );
  }, [booking?.bookedTrips, changeFlow, leg.id]);

  const previouslyBookedAncillaries = ancillaryRows.filter(
    ({ isPreviouslyBooked, isVisible }) => isVisible && isPreviouslyBooked
  );

  const addedAncillaries = ancillaryRows.filter(
    ({ isPreviouslyBooked, isVisible }) => isVisible && !isPreviouslyBooked
  );

  return (
    <div className="flex flex-col gap-2">
      <LegOriginDestination
        origin={leg.originStop?.name ?? ''}
        destination={leg.destinationStop?.name ?? ''}
        ptMode={leg.ptMode}
      />
      {!!previouslyBookedAncillaries.length && (
        <LegSectionAncillaryAccordion
          title={
            <TransText
              i18nKey="previouslyBookedExtras"
              values={{ count: previouslyBookedAncillaries.length }}
            />
          }
          ancillaryRows={previouslyBookedAncillaries}
          classNames={classNames?.accordionItem}
          handleRemoveAncillary={handleRemoveAncillary}
        />
      )}
      {!!addedAncillaries.length && (
        <LegSectionAncillaryAccordion
          title={<TransText i18nKey="addedExtras" />}
          ancillaryRows={addedAncillaries}
          classNames={classNames?.accordionItem}
          handleRemoveAncillary={handleRemoveAncillary}
        />
      )}

      <Button
        variant="secondary"
        data-testid="add-ancillary-button"
        className="mt-1 w-fit"
        onClick={changeSelectedLeg}
        isDisabled={!availableAncillaries.length}
        startAdornment={<Icons.addon className="h-5 w-5" />}
      >
        <TransText
          i18nKey={
            availableAncillaries.length ? 'addExtras' : 'noExtrasAvailable'
          }
        />
      </Button>
    </div>
  );
};

export default LegSection;
